import {
  Body1,
  Button,
  Caption2,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  mergeClasses,
  Subtitle2,
  Tooltip,
} from '@fluentui/react-components'
import { MoreHorizontal20Regular, Star20Filled, Star20Regular } from '@fluentui/react-icons'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { useApplyPrompt, useDeletePrompt, useToggleFavoritePrompt } from '../../hooks'
import type { ReviewMode } from '../../types'
import { Dialog } from '../Dialog/Dialog'
import { useStyles } from './PromptCard.styles'

export interface PromptCardProps {
  type: 'standard' | 'custom'
  id: string
  title: string
  previewText: string
  promptToInsert?: string
  isFavorite: boolean
  reviewMode?: ReviewMode | null
  requiresDocumentComments?: boolean
  llmModel: string
  systemPrompt?: string
  requiresFullDocument: boolean
}

const PromptCard = ({
  type,
  id,
  title,
  previewText,
  promptToInsert,
  isFavorite,
  reviewMode,
  requiresDocumentComments = false,
  llmModel,
  systemPrompt,
  requiresFullDocument,
}: PromptCardProps) => {
  const classes = useStyles()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { applyPrompt } = useApplyPrompt({ fetchDocumentName: requiresFullDocument })
  const { mutateAsync: deletePrompt, isPending: isDeleting } = useDeletePrompt()
  const {
    mutate: toggleFavorite,
    isPending: isTogglingFavorite,
    variables,
  } = useToggleFavoritePrompt()

  // Flip the star instantly while the API call is in flight; reverts if it fails
  const displayedIsFavorite =
    isTogglingFavorite && variables?.promptId === id ? !isFavorite : isFavorite
  const favoritingActionLabel = displayedIsFavorite
    ? t('prompts.card.unfavorite')
    : t('prompts.card.favorite')

  const [isActive, setIsActive] = useState(false)
  const [isDialogOpen, setIsDialogOpen] = useState(false)

  // Keep the more-actions button mounted while the menu is open so Fluent UI
  // retains the trigger reference when the popover renders into a portal
  const [isMenuOpen, setIsMenuOpen] = useState(false)

  // isActive covers both mouse hover and keyboard focus so buttons are always reachable
  const showActions = isActive || isMenuOpen
  const showStar = displayedIsFavorite || showActions

  const handleBlur = (e: React.FocusEvent) => {
    // Only deactivate when focus leaves the card entirely (not moving to an inner button)
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsActive(false)
    }
  }

  const onDialogOpenChange = useCallback((open: boolean) => {
    setIsDialogOpen(open)
  }, [])

  const onDeleteConfirm = useCallback(async () => {
    await deletePrompt({ promptId: id })
    setIsDialogOpen(false)
  }, [deletePrompt, id])

  const handlePromptClick = (e: React.MouseEvent | React.KeyboardEvent) => {
    // Ignore clicks from inner or dialog buttons; data-action is an explicit opt-in for portal-rendered
    // elements (e.g. MenuItem) whose DOM node lives outside the card but still bubbles via React
    if ((e.target as HTMLElement).closest('button, [data-action]')) return
    // Dialog backdrop is portal-rendered so it won't match .closest above; block while dialog is open
    if (isDialogOpen) return
    applyPrompt({
      promptId: id,
      title,
      description: previewText,
      userPrompt: promptToInsert ?? previewText,
      llmModel,
      promptType: type,
      isFavorite,
      systemPrompt,
      reviewMode: reviewMode ?? null,
      requiresDocumentComments,
      requiresFullDocument,
    })
    navigate('/')
  }

  return (
    <div
      className={classes.card}
      role="button"
      tabIndex={0}
      onClick={handlePromptClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') handlePromptClick(e)
      }}
      onMouseEnter={() => setIsActive(true)}
      onMouseLeave={() => setIsActive(false)}
      onFocus={(e) => {
        // Activate whenever focus enters from outside the card (Tab or Shift+Tab),
        // but not when focus moves between elements inside (e.g. clicking a child button)
        if (!e.currentTarget.contains(e.relatedTarget as Node)) setIsActive(true)
      }}
      onBlur={handleBlur}>
      <div className={classes.titleSection}>
        <div className={classes.titleRow}>
          <Subtitle2 as="p" className={classes.title}>
            {title}
          </Subtitle2>
          <div
            className={mergeClasses(
              classes.iconGroup,
              (showActions || displayedIsFavorite) && classes.iconGroupVisible
            )}>
            {/* Always rendered for custom type so the Menu trigger stays mounted
                while the popover is open; hidden via visibility when not active */}
            {type === 'custom' && (
              <div
                className={mergeClasses(
                  classes.actionSlot,
                  showActions && classes.actionSlotVisible
                )}>
                <Menu onOpenChange={(_, data) => setIsMenuOpen(data.open)}>
                  <MenuTrigger disableButtonEnhancement>
                    <Button
                      appearance="transparent"
                      aria-label={t('prompts.card.moreActions')}
                      icon={<MoreHorizontal20Regular />}
                      className={mergeClasses(
                        classes.buttonHidden,
                        showActions && classes.buttonVisible
                      )}
                    />
                  </MenuTrigger>
                  <MenuPopover>
                    <MenuList>
                      {/* Uncomment with # 3798 */}
                      {/* <MenuItem data-action onClick={() => console.log('edit prompt', title)}>
                        {t('prompts.card.edit')}
                      </MenuItem> */}
                      <MenuItem data-action onClick={() => setIsDialogOpen(true)}>
                        {t('delete')}
                      </MenuItem>
                    </MenuList>
                  </MenuPopover>
                </Menu>
              </div>
            )}
            {/* Always in DOM so keyboard users can Tab to it; visibility controls display */}
            <Tooltip content={favoritingActionLabel} relationship="label" appearance="inverted">
              <Button
                appearance="transparent"
                // describe action (opposite of current state)
                aria-label={favoritingActionLabel}
                aria-pressed={displayedIsFavorite}
                icon={
                  displayedIsFavorite ? (
                    <Star20Filled className={classes.star} data-testid="star-filled" />
                  ) : (
                    <Star20Regular className={classes.star} data-testid="star-regular" />
                  )
                }
                className={mergeClasses(classes.buttonHidden, showStar && classes.buttonVisible)}
                disabled={isTogglingFavorite}
                onClick={() => toggleFavorite({ promptId: id })}
              />
            </Tooltip>
          </div>
        </div>
        <Body1
          as="p"
          className={mergeClasses(
            classes.grayParagraph,
            type === 'custom' && classes.promptClamped
          )}>
          {previewText}
        </Body1>
      </div>
      {type === 'custom' && (
        <Caption2 as="p" className={classes.grayParagraph}>
          {t('prompts.card.createdByYou')}
        </Caption2>
      )}
      <Dialog
        title={t('dialog.deletePromptDialogTitle')}
        content={t('dialog.deletePromptDialogContent', { title })}
        primaryButtonText={t('delete')}
        secondaryButtonText={t('cancel')}
        onConfirm={onDeleteConfirm}
        onOpenChange={onDialogOpenChange}
        isDialogOpen={isDialogOpen}
        isLoading={isDeleting}
        loadingLabel={t('dialog.deletePromptDialogLoading')}
      />
    </div>
  )
}

export { PromptCard }
